import { db } from '../lib/firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  DocumentReference,
  DocumentData
} from 'firebase/firestore';
import { GasPod, CreateGasPodData, GasContract, CreateGasContractData, GasMeterReading, CreateGasMeterReadingData, GasInvoice, CreateGasInvoiceData, GasNomination, CreateGasNominationData } from '../types/gas';

const GAS_PODS_COLLECTION = 'gas_pods';
const GAS_CONTRACTS_COLLECTION = 'gas_contracts';
const GAS_METER_READINGS_COLLECTION = 'gas_meter_readings';
const GAS_INVOICES_COLLECTION = 'gas_invoices';
const GAS_NOMINATIONS_COLLECTION = 'gas_nominations';

const gasPodsCollection = collection(db, GAS_PODS_COLLECTION);
const gasContractsCollection = collection(db, GAS_CONTRACTS_COLLECTION);
const gasMeterReadingsCollection = collection(db, GAS_METER_READINGS_COLLECTION);
const gasInvoicesCollection = collection(db, GAS_INVOICES_COLLECTION);
const gasNominationsCollection = collection(db, GAS_NOMINATIONS_COLLECTION);

export const createGasPod = async (data: CreateGasPodData): Promise<GasPod> => {
  const docRef = await addDoc(gasPodsCollection, {
    ...data,
    status: data.status || 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return { ...data, id: docRef.id, status: data.status || 'active', createdAt: new Date() } as GasPod;
};

export const getGasPodById = async (id: string): Promise<GasPod | null> => {
  const docRef = doc(db, GAS_PODS_COLLECTION, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as GasPod;
};

export const getGasPodsByCustomerId = async (customerId: string): Promise<GasPod[]> => {
  const q = query(
    gasPodsCollection,
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasPod);
};

export const getAllGasPods = async (): Promise<GasPod[]> => {
  const q = query(gasPodsCollection, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasPod);
};

export const updateGasPod = async (id: string, data: Partial<CreateGasPodData>): Promise<void> => {
  const docRef = doc(db, GAS_PODS_COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteGasPod = async (id: string): Promise<void> => {
  const docRef = doc(db, GAS_PODS_COLLECTION, id);
  await deleteDoc(docRef);
};

export const createGasContract = async (data: CreateGasContractData): Promise<GasContract> => {
  const docRef = await addDoc(gasContractsCollection, {
    ...data,
    status: data.status || 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return { ...data, id: docRef.id, status: data.status || 'active', createdAt: new Date() } as GasContract;
};

export const getGasContractById = async (id: string): Promise<GasContract | null> => {
  const docRef = doc(db, GAS_CONTRACTS_COLLECTION, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as GasContract;
};

export const getGasContractsByCustomerId = async (customerId: string): Promise<GasContract[]> => {
  const q = query(
    gasContractsCollection,
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasContract);
};

export const getAllGasContracts = async (): Promise<GasContract[]> => {
  const q = query(gasContractsCollection, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasContract);
};

export const updateGasContract = async (id: string, data: Partial<CreateGasContractData>): Promise<void> => {
  const docRef = doc(db, GAS_CONTRACTS_COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteGasContract = async (id: string): Promise<void> => {
  const docRef = doc(db, GAS_CONTRACTS_COLLECTION, id);
  await deleteDoc(docRef);
};

export const gasPodService = {
  createGasPod,
  getGasPodById,
  getGasPodsByCustomerId,
  getAllGasPods,
  updateGasPod,
  deleteGasPod
};

export const gasContractService = {
  createGasContract,
  getGasContractById,
  getGasContractsByCustomerId,
  getAllGasContracts,
  updateGasContract,
  deleteGasContract
};

export const createGasMeterReading = async (data: CreateGasMeterReadingData): Promise<GasMeterReading> => {
  const docRef = await addDoc(gasMeterReadingsCollection, {
    ...data,
    status: data.status || 'read',
    source: data.source || 'manual',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return { ...data, id: docRef.id, status: data.status || 'read', source: data.source || 'manual', createdAt: new Date() } as GasMeterReading;
};

export const getGasMeterReadingById = async (id: string): Promise<GasMeterReading | null> => {
  const docRef = doc(db, GAS_METER_READINGS_COLLECTION, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as GasMeterReading;
};

export const getGasMeterReadingsByPodId = async (gasPodId: string): Promise<GasMeterReading[]> => {
  const q = query(
    gasMeterReadingsCollection,
    where('gasPodId', '==', gasPodId),
    orderBy('gasDay', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasMeterReading);
};

export const getGasMeterReadingsByCustomerId = async (customerId: string): Promise<GasMeterReading[]> => {
  const q = query(
    gasMeterReadingsCollection,
    where('customerId', '==', customerId),
    orderBy('gasDay', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasMeterReading);
};

export const getAllGasMeterReadings = async (): Promise<GasMeterReading[]> => {
  const q = query(gasMeterReadingsCollection, orderBy('gasDay', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasMeterReading);
};

export const updateGasMeterReading = async (id: string, data: Partial<CreateGasMeterReadingData>): Promise<void> => {
  const docRef = doc(db, GAS_METER_READINGS_COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteGasMeterReading = async (id: string): Promise<void> => {
  const docRef = doc(db, GAS_METER_READINGS_COLLECTION, id);
  await deleteDoc(docRef);
};

export const gasMeterReadingService = {
  createGasMeterReading,
  getGasMeterReadingById,
  getGasMeterReadingsByPodId,
  getGasMeterReadingsByCustomerId,
  getAllGasMeterReadings,
  updateGasMeterReading,
  deleteGasMeterReading
};

export const createGasInvoice = async (data: CreateGasInvoiceData): Promise<GasInvoice> => {
  const docRef = await addDoc(gasInvoicesCollection, {
    ...data,
    status: data.status || 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return { ...data, id: docRef.id, status: data.status || 'draft', createdAt: new Date() } as GasInvoice;
};

export const getGasInvoiceById = async (id: string): Promise<GasInvoice | null> => {
  const docRef = doc(db, GAS_INVOICES_COLLECTION, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as GasInvoice;
};

export const getGasInvoicesByCustomerId = async (customerId: string): Promise<GasInvoice[]> => {
  const q = query(
    gasInvoicesCollection,
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasInvoice);
};

export const getAllGasInvoices = async (): Promise<GasInvoice[]> => {
  const q = query(gasInvoicesCollection, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasInvoice);
};

export const updateGasInvoice = async (id: string, data: Partial<CreateGasInvoiceData>): Promise<void> => {
  const docRef = doc(db, GAS_INVOICES_COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteGasInvoice = async (id: string): Promise<void> => {
  const docRef = doc(db, GAS_INVOICES_COLLECTION, id);
  await deleteDoc(docRef);
};

export const gasInvoiceService = {
  createGasInvoice,
  getGasInvoiceById,
  getGasInvoicesByCustomerId,
  getAllGasInvoices,
  updateGasInvoice,
  deleteGasInvoice
};

export const createGasNomination = async (data: CreateGasNominationData): Promise<GasNomination> => {
  const docRef = await addDoc(gasNominationsCollection, {
    ...data,
    status: data.status || 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return { ...data, id: docRef.id, status: data.status || 'draft', createdAt: new Date() } as GasNomination;
};

export const getGasNominationById = async (id: string): Promise<GasNomination | null> => {
  const docRef = doc(db, GAS_NOMINATIONS_COLLECTION, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as GasNomination;
};

export const getGasNominationsByCustomerId = async (customerId: string): Promise<GasNomination[]> => {
  const q = query(
    gasNominationsCollection,
    where('customerId', '==', customerId),
    orderBy('gasDay', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasNomination);
};

export const getAllGasNominations = async (): Promise<GasNomination[]> => {
  const q = query(gasNominationsCollection, orderBy('gasDay', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as GasNomination);
};

export const updateGasNomination = async (id: string, data: Partial<CreateGasNominationData>): Promise<void> => {
  const docRef = doc(db, GAS_NOMINATIONS_COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteGasNomination = async (id: string): Promise<void> => {
  const docRef = doc(db, GAS_NOMINATIONS_COLLECTION, id);
  await deleteDoc(docRef);
};

export const gasNominationService = {
  createGasNomination,
  getGasNominationById,
  getGasNominationsByCustomerId,
  getAllGasNominations,
  updateGasNomination,
  deleteGasNomination
};

export default gasPodService;
